import { h, defineComponent, ref, watch } from 'vue';
import { d, t } from '@/ui/i18n/i18n';
import { box, indexView, loader, pagination, textColumn } from '@/ui/components';
import { list as getSensorList, remove } from '@/services/api/resources/sensor';
import { emptyList } from '@/services/api';
import { SensorList, SensorRecord } from '@/entities/sensor';
import { usePagination, useSorting, useTask } from '@/ui/concerns';
import { ResourceTable } from '@/ui/components/table/ResourceTable';
import { hasFeature, hasPermission, Permissions } from '@/ui/auth/auth';
import { numericColumn } from '@/ui/components/table/TableColumn';
import { renderForbiddenPage } from '@/ui/pages/error/ForbiddenPage';
import { SortDirection } from '@/ui/types';
import * as qs from '@/ui/queryState';
import { searchFilter, entityItemFilter } from '@/ui/components/filters/filters-horizontal';
import { useFilters } from '@/ui/pages/sensor/useFilters';
import { useSensorFormat } from '@/ui/pages/sensor/useSensorFormat';
import { filterFormLoader } from '@/ui/components/Loader';
import { renderResourceTeaserPage } from '@/ui/pages/teasers/ResourceTeaserPage';

export const SensorIndexPage = defineComponent({
  name: 'SensorIndexPage',
  setup() {
    if (!hasFeature('sensors')) {
      return renderResourceTeaserPage(t('common.sensors'));
    }

    if (!hasPermission(Permissions.MANAGE_HARDWARE)) {
      return renderForbiddenPage;
    }

    const initialLoading = ref(true);

    // state stored in query string
    const { page, perPage, resetPagination } = usePagination();
    const { sort, dir } = useSorting('lastMeasuredAt', SortDirection.Descending);
    const search = qs.string('search', '');
    const entityItem = qs.numbers('entityItem', []);

    // get list task
    const list = ref<SensorList>(emptyList());
    const getList = useTask(
      () => getSensorList({
        page: page.value,
        perPage: perPage.value,
        sort: sort.value,
        dir: dir.value,
        search: search.value,
        entityItem: entityItem.value,
      }),
      listResponse => {
        list.value = listResponse;
        initialLoading.value = false;
      },
    );
    watch([page, perPage, entityItem, search], getList.run);

    const { entityItems, getFilters } = useFilters();
    const { formatMetric } = useSensorFormat();

    return function render() {
      return h('div', [
        indexView(
          [
            filterFormLoader(getFilters, [
              searchFilter(search, resetPagination),
              entityItemFilter(entityItem, entityItems.value, resetPagination),
            ]),
          ],
          [
            box([
              loader('div', getList, [
                h(ResourceTable, {
                  props: {
                    editRoute: 'sensorMeasurements',
                    list: list.value.data,
                    deleteFn: remove,
                    sort: sort.value,
                    dir: dir.value,
                  },
                  scopedSlots: {
                    title: ({ row: sensor }: { row: SensorRecord }) => sensor.data.title,
                    default: () => [
                      numericColumn({
                        field: 'temperature',
                        label: t('sensors.temperature'),
                      }, {
                        default: ({ row: sensor }: { row: SensorRecord }) => [formatMetric(
                          sensor.data.temperatureLastMeasurement,
                          sensor.data.temperatureLowerBound,
                          sensor.data.temperatureUpperBound,
                          'metricTemperature',
                        )],
                      }),
                      numericColumn({
                        field: 'humidity',
                        label: t('sensors.humidity'),
                      }, {
                        default: ({ row: sensor }: { row: SensorRecord }) => [formatMetric(
                          sensor.data.humidityLastMeasurement,
                          sensor.data.humidityLowerBound,
                          sensor.data.humidityUpperBound,
                          'metricHumidity',
                        )],
                      }),
                      textColumn({
                        field: 'lastMeasuredAt',
                        label: t('sensors.last-measured-at'),
                      }, {
                        default: ({ row: sensor }: { row: SensorRecord }) => sensor.data.lastMeasuredAt
                          ? d(sensor.data.lastMeasuredAt, 'shortDateTime')
                          : '',
                      }),
                    ],
                  },
                }),
                pagination({
                  current: page.value,
                  total: list.value.meta.total,
                  perPage: perPage.value,
                }),
              ], !initialLoading.value),
            ]),
          ],
        ),
      ]);
    };
  },
});
